import type { DomainCageOverviewEnvironmental } from '@/domain/cages/overview.types';
import type { ExtraExportSheet } from '@/hooks/download-CSV-XLSX';
import { roundToDecimals } from '@/utils/localeFormatters';

type Translate = (key: string, options: { ns: 'environmentalData' }) => string;

/**
 * Cage overview download options. When there is no hourly snapshot the caller
 * must omit both props so the XLSX stays a single sheet named `data`.
 */
export function cageOverviewExportOptions(
    environmental: DomainCageOverviewEnvironmental | null,
    translate: Translate
): { sheetName?: string; extraSheet?: ExtraExportSheet } {
    const extraSheet = buildEnvironmentalExportSheet(environmental, translate);
    if (extraSheet == null) {
        return {};
    }

    return {
        sheetName: translate('export.overviewSheet', { ns: 'environmentalData' }),
        extraSheet,
    };
}

function buildEnvironmentalExportSheet(
    environmental: DomainCageOverviewEnvironmental | null,
    translate: Translate
): ExtraExportSheet | undefined {
    if (environmental == null) {
        return undefined;
    }

    const tEnv = (key: string) => translate(key, { ns: 'environmentalData' });
    const cell = (value: number | undefined): string | number =>
        value == null ? '' : roundToDecimals(value, 1);

    return {
        name: tEnv('export.hourlySheet'),
        header: [
            tEnv('export.lastMeasurement'),
            tEnv('export.oxygenSaturation'),
            tEnv('export.oxygenConcentration'),
            tEnv('export.salinity'),
            tEnv('export.temperature'),
            tEnv('export.averageDepth'),
        ],
        rows: [
            [
                // The site's wall clock as the card shows it, not an instant.
                environmental.lastMeasurement,
                cell(environmental.oxygenSaturation),
                cell(environmental.oxygenConcentration),
                cell(environmental.salinity),
                cell(environmental.temperature),
                cell(environmental.depth),
            ],
        ],
    };
}
